// Pure logic that turns the four analytics tab blocks into the normalized
// "observation card" model the unified report renders. Kept framework-free so
// the health/verdict/severity rules are easy to read and tune in one place.
//
// Russian verdict sentences are inlined here (not in i18n) on purpose: they are
// dynamic, data-driven phrasings — exactly like the BE daily-report sections —
// and threading every interpolation through the i18n dictionary would add noise
// without value. Static chrome labels still go through i18n in the components.

import type {
  TablesAnalytics,
  TasksAnalytics,
  LayoutFindings,
  LayoutCategoryFinding,
  QueueFindings,
  LayoutDetailShelfTimeseries,
} from '@/api/analytics';

export type AreaKey = 'layout' | 'queue' | 'tables' | 'tasks';
export type Health = 'red' | 'yellow' | 'green';

export interface ReportPhoto {
  image_url: string | null;
  caption?: string;
  at?: string | null;
  fill_pct?: number | null;
  captured_occupancy?: number | null;
  rack_name?: string;
}

export interface TimelinePoint {
  date: string;
  value: number | null;
}

export interface AreaCard {
  key: AreaKey;
  /** Stable React key. Defaults to `key`, but the layout section emits several
   *  cards that all share key='layout' (one per problem category), so they need
   *  a distinct id. */
  cardId?: string;
  title: string;
  health: Health;
  /** Cross-area sort/hero-selection score; health dominates, magnitude breaks ties. */
  score: number;
  /** One plain-Russian sentence — the headline verdict. */
  verdict: string;
  /** Optional supporting line (baseline / second fact). */
  subVerdict?: string;
  timeseries: TimelinePoint[];
  /** Y-axis/value unit for the mini timeline ('%' for fill, '' for people). */
  unit: string;
  /** 0..100 Y domain cap (fill) vs auto (people/seconds). */
  yMax?: number;
  photo?: ReportPhoto | null;
  /** Stable color for the timeline stroke. */
  color: string;
  /** Deepest escape hatch — shown at the foot of the drill-down only. */
  grafanaUrl?: string | null;
  /** Layout only: per-shelf stacked timeline of the worst rack — rendered
   *  instead of the flat single line so the day's dips show at a glance. */
  shelfTimeseries?: LayoutDetailShelfTimeseries | null;
}

const HEALTH_RANK: Record<Health, number> = { red: 0, yellow: 1, green: 2 };

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function scoreFor(health: Health, magnitude: number): number {
  const base = health === 'red' ? 200 : health === 'yellow' ? 100 : 0;
  return base + clamp(magnitude, 0, 99);
}

// ── formatters ────────────────────────────────────────────────────────────────

export function fmtHours(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '';
  if (minutes < 60) return `${Math.round(minutes)} мин`;
  const h = minutes / 60;
  // One decimal, comma separator (RU), drop trailing ",0".
  const s = h.toFixed(1).replace('.', ',');
  return `${s.endsWith(',0') ? s.slice(0, -2) : s} ч`;
}

export function fmtCleanup(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)} с`;
  const m = Math.floor(seconds / 60);
  const r = Math.round(seconds % 60);
  return r === 0 ? `${m} мин` : `${m} мин ${r} с`;
}

// ── per-area builders ───────────────────────────────────────────────────────

// Layout is special: the TG daily report (compute_overall_performance) never
// averages across rack categories — it reports готовая еда / выпечка / десерты
// each on its own line, with its own severity, #shelves, avg shortage % and
// problem-hours. The day-findings endpoint returns the same `categories[]`, so
// the layout section is rendered as ONE row per *problem* category (matching the
// TG message), not a single averaged headline. When nothing is wrong, a single
// green summary card stands in — collapsed, but expand-to-prove via the worst
// category's photo + full-day shelf chart.

/** Verdict + subVerdict for one problem layout category, mirroring the TG line
 *  "🟠 Готовая еда: по 2 полкам средняя нехватка 45%. Проблемы … 5.0ч." */
function layoutCategoryVerdict(cat: LayoutCategoryFinding): { verdict: string; subVerdict?: string } {
  const shortage = cat.shortage_pct ?? 0;
  const hours = cat.problem_hours ?? 0;
  const verdict = `${cat.rack_type}: нехватка ${Math.round(shortage)}%`;
  const dur = fmtHours(hours * 60);
  const shelfWord =
    cat.shelf_count != null
      ? `по ${cat.shelf_count} ${plural(cat.shelf_count, 'полке', 'полкам', 'полкам')}`
      : '';
  // "по N полкам · проблемы 5 ч за день" — incident framing, never averaged.
  const parts = [shelfWord, dur ? `проблемы ${dur} за день` : ''].filter(Boolean);
  return { verdict, subVerdict: parts.length ? parts.join(' · ') : undefined };
}

/**
 * One card per PROBLEM layout category (готовая еда / выпечка / десерты …), in
 * the TG report's priority order. The worst category (the one the endpoint
 * anchored its photo + shelf chart to) carries that proof; the others carry the
 * worst-category hourly line as a fallback shape. Returns [] when the layout
 * isn't configured. When configured but all-green, returns a single calm summary
 * card so the section still proves the calm on expand.
 */
export function buildLayoutCategoryCards(layout: LayoutFindings | undefined): AreaCard[] {
  if (!layout || layout.configured === false) return [];

  const ts: TimelinePoint[] = (layout.timeseries ?? []).map((p) => ({ date: p.date, value: p.value }));
  const cats = layout.categories ?? [];
  const worst = layout.worst ?? null;
  const problems = cats.filter((c) => c.status !== 'green');

  // All calm → a single green summary card. Expand proves it: the worst-rack
  // shelf chart + representative photo still ride along when the endpoint has
  // them (they're null on a genuinely calm day, which is fine).
  if (problems.length === 0) {
    return [
      {
        key: 'layout',
        title: 'Выкладка',
        health: 'green',
        score: scoreFor('green', 0),
        verdict: 'Полки в норме весь день',
        timeseries: ts,
        unit: '%',
        yMax: 100,
        photo: layout.photo ?? null,
        color: '#6366f1',
        grafanaUrl: layout.grafana_url ?? null,
        shelfTimeseries: layout.shelf_timeseries ?? null,
      },
    ];
  }

  // Priority order matches the endpoint's worst-pick + TG report emphasis.
  const PRIORITY = ['Готовая еда', 'Выпечка', 'Десерты'];
  const rank = (rt: string) => {
    const i = PRIORITY.indexOf(rt);
    return i === -1 ? PRIORITY.length : i;
  };
  const ordered = [...problems].sort((a, b) => {
    const dr = HEALTH_RANK[a.status as Health] - HEALTH_RANK[b.status as Health];
    if (dr !== 0) return dr;
    const pr = rank(a.rack_type) - rank(b.rack_type);
    if (pr !== 0) return pr;
    return (b.shortage_pct ?? 0) - (a.shortage_pct ?? 0);
  });

  return ordered.map((cat, idx) => {
    const isWorst = worst != null && cat.rack_type === worst.rack_type;
    const { verdict, subVerdict } = layoutCategoryVerdict(cat);
    const health = cat.status as Health;
    const magnitude = (cat.shortage_pct ?? 0) + (cat.problem_hours ?? 0) * 4;
    return {
      // Unique React key per category card while keeping the area key for
      // drilldown/section grouping.
      key: 'layout' as AreaKey,
      cardId: `layout:${cat.rack_type || idx}`,
      title: 'Выкладка',
      health,
      score: scoreFor(health, magnitude),
      verdict,
      subVerdict,
      timeseries: ts,
      unit: '%',
      yMax: 100,
      // Only the worst category carries the anchored proof photo + shelf chart;
      // the endpoint computes them once (for the worst). Others show the line.
      photo: isWorst ? (layout.photo ?? null) : null,
      color: '#6366f1',
      grafanaUrl: layout.grafana_url ?? null,
      shelfTimeseries: isWorst ? (layout.shelf_timeseries ?? null) : null,
    };
  });
}

export function buildQueueCard(queue: QueueFindings | undefined): AreaCard | null {
  if (!queue || queue.configured === false) return null;
  const peak = queue.peak_people ?? 0;
  const ts: TimelinePoint[] = (queue.timeseries ?? []).map((p) => ({ date: p.date, value: p.value }));

  const health: Health = (queue.status ?? 'green') as Health;
  const magnitude = (peak - 4) * 15;

  let verdict: string;
  if (peak <= 0) verdict = 'Очередь не скапливалась';
  else {
    verdict = `Очередь до ${peak} ${plural(peak, 'человека', 'человек', 'человек')} в пик`;
    if (queue.decrease_min) verdict += `, разошлась за ${queue.decrease_min} мин`;
  }
  // The "no visitors N of M hours" line is the team-idle signal Alexey valued.
  const subVerdict =
    queue.idle_hours != null && queue.open_hours
      ? `гостей не было ${queue.idle_hours} ч из ${queue.open_hours}`
      : undefined;

  return {
    key: 'queue',
    title: 'Очередь',
    health,
    score: scoreFor(health, magnitude),
    verdict,
    subVerdict,
    timeseries: ts,
    unit: ' чел',
    photo: queue.photo ?? null,
    color: '#f59e0b',
    grafanaUrl: queue.grafana_url ?? null,
  };
}

export function buildTablesCard(tables: TablesAnalytics | undefined): AreaCard | null {
  if (!tables || !tables.total_tables) return null;
  const median = tables.median_cleanup_s;
  const unresolved = tables.unresolved_count ?? 0;
  const ts: TimelinePoint[] = (tables.timeseries ?? []).map((p) => ({
    // tables timeseries values are seconds → minutes for readability
    date: p.date,
    value: p.value == null ? null : Math.round((p.value / 60) * 10) / 10,
  }));

  let health: Health = 'green';
  let magnitude = 0;
  if (median == null && unresolved === 0) {
    // configured but no cleanup events in the period → genuinely calm
    health = 'green';
  } else {
    if (unresolved > 0 || (median ?? 0) > 900) health = 'red';
    else if ((median ?? 0) > 300) health = 'yellow';
    magnitude = (median ?? 0) / 20;
  }

  let verdict: string;
  if (unresolved > 0) verdict = `${unresolved} ${plural(unresolved, 'стол', 'стола', 'столов')} остались грязными`;
  else if (median == null) verdict = 'Грязных столов за день не было';
  else verdict = `Убирали за ${fmtCleanup(median)} (медиана)`;
  const subVerdict =
    tables.p90_cleanup_s != null ? `почти всегда не дольше ${fmtCleanup(tables.p90_cleanup_s)}` : undefined;

  return {
    key: 'tables',
    title: 'Столы',
    health,
    score: scoreFor(health, magnitude),
    verdict,
    subVerdict,
    timeseries: ts,
    unit: ' мин',
    photo: null,
    color: '#ef4444',
  };
}

export function buildTasksCard(tasks: TasksAnalytics | undefined): AreaCard | null {
  if (!tasks || !tasks.total) return null;
  const rate = tasks.completion_rate ?? 0;
  const ts: TimelinePoint[] = (tasks.timeseries ?? []).map((p) => ({ date: p.date, value: p.rate }));

  let health: Health = 'green';
  if (rate < 70) health = 'red';
  else if (rate < 90) health = 'yellow';
  const magnitude = 100 - rate;

  const late = tasks.total - tasks.done;
  const verdict = `${tasks.done}/${tasks.total} ${plural(tasks.total, 'задача', 'задачи', 'задач')} выполнено`;
  const subVerdict =
    late > 0
      ? `${late} ${plural(late, 'не выполнена', 'не выполнены', 'не выполнено')}`
      : tasks.on_time_rate != null
        ? `${tasks.on_time_rate}% вовремя`
        : undefined;

  return {
    key: 'tasks',
    title: 'Задачи',
    health,
    score: scoreFor(health, magnitude),
    verdict,
    subVerdict,
    timeseries: ts,
    unit: '%',
    yMax: 100,
    photo: null,
    color: '#22c55e',
  };
}

// ── assembly ──────────────────────────────────────────────────────────────────

export function sortCards(cards: AreaCard[]): AreaCard[] {
  return [...cards].sort((a, b) => {
    const dr = HEALTH_RANK[a.health] - HEALTH_RANK[b.health];
    if (dr !== 0) return dr;
    return b.score - a.score;
  });
}

/** Russian numeric plural: (1, 'стол','стола','столов'). */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
